import React, { useState, useEffect } from "react";
import { fetchUsersByRole } from "../../../api";
import { USER_ROLES } from "../../constants";
import { FaUserFriends } from "react-icons/fa";
import { IoSearchOutline } from "react-icons/io5";
import "../PatientsList/PatientsList.css";

const DoctorList = ({ selectedDoctorId, onDoctorClick }) => {
  const [doctors, setDoctors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchInput, setSearchInput] = useState("");

  const loadDoctors = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await fetchUsersByRole(USER_ROLES.DOCTOR);
      // Handle different response structures
      const doctorsList = Array.isArray(response) 
        ? response 
        : (response?.users || response?.data || []);
      setDoctors(doctorsList);
    } catch (error) {
      console.error("Error loading doctors:", error);
      setError("Failed to load doctors");
      setDoctors([]); // Ensure doctors is always an array
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDoctors();
  }, []);

  const handleDoctorClick = (doctor) => {
    if (onDoctorClick) {
      onDoctorClick(doctor);
    }
  };

  const getAllDoctors = () => {
    return doctors || [];
  };

  const getFilteredDoctors = () => {
    let filteredDoctors = getAllDoctors();

    // No active/inactive filtering since number_of_videos is not in API response
    filteredDoctors = doctors;

    if (searchInput) {
      filteredDoctors = filteredDoctors.filter(
        (doctor) =>
          doctor.full_name
            ?.toLowerCase()
            .includes(searchInput.toLowerCase()) ||
          doctor.email?.toLowerCase().includes(searchInput.toLowerCase()) ||
          doctor.username?.toLowerCase().includes(searchInput.toLowerCase())
      );
    }

    return filteredDoctors;
  };

  const getFilteredCounts = () => {
    const allDoctors = doctors || [];

    const filteredAll = searchInput
      ? allDoctors.filter(
          (doctor) =>
            doctor.full_name
              ?.toLowerCase()
              .includes(searchInput.toLowerCase()) ||
            doctor.email?.toLowerCase().includes(searchInput.toLowerCase()) ||
            doctor.username?.toLowerCase().includes(searchInput.toLowerCase())
        )
      : allDoctors;

    return {
      total: filteredAll.length,
      active: 0,
      inactive: 0,
    };
  };

  if (loading) {
    return (
      <div className="patients-container">
        <div className="loading-message">Loading doctors...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="patients-container">
        <div className="error-message">
          <p>{error}</p>
          <button onClick={loadDoctors}>Retry</button>
        </div>
      </div>
    );
  }

  const filteredDoctors = getFilteredDoctors();
  const filteredCounts = getFilteredCounts();

  return (
    <div className="patients-container">
      <div className="stats-row">
        <div className="stat-card">
          <div className="stat-icon-wrapper first">
            <FaUserFriends className="stat-icon" />
          </div>
          <div className="stat-content">
            <span className="stat-label">
              Total Doctors {searchInput && "(filtered)"}
            </span>
            <span className="stat-value">{filteredCounts.total}</span>
          </div>
        </div>
      </div>

      <div className="patients-table-container">
        <div className="table-header">
          <div className="table-controls">
            <div className="search-container">
              <IoSearchOutline className="search-icon" />
              <input
                placeholder="Search doctors..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                className="search-input"
              />
            </div>
          </div>
        </div>

        <div className="patients-table">
          <div className="table-header-row three-columns">
            <span className="header-cell">Name</span>
            <span className="header-cell">Email</span>
            <span className="header-cell">Actions</span>
          </div>

          {filteredDoctors.map((doctor, index) => (
            <div key={doctor.username || doctor.email || index} className="table-row three-columns">
              <span className="table-cell patient-name">
                {doctor.full_name}
              </span>
              <span className="table-cell">{doctor.email || 'N/A'}</span>
              <span className="table-cell">
                <button
                  className="action-button"
                  onClick={() => handleDoctorClick(doctor)}
                >
                  View
                </button>
              </span>
            </div>
          ))}

          {filteredDoctors.length === 0 && (
            <div className="no-data-row">
              <span className="no-data-message">
                {searchInput
                  ? "No doctors found matching your search."
                  : "No doctors available."}
              </span>
            </div>
          )}
        </div>

        <div className="table-footer">
          <span className="table-info">
            Showing {filteredDoctors.length} of {filteredCounts.total} doctors
          </span>
        </div>
      </div>
    </div>
  );
};

export default DoctorList;
